import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { closeTopBarMenu, useTopBarMenuHosted, useTopBarMenuSlot } from '@/shared/components/topBarMenuSlot'

/** One row of the overflow menu: icon + text + the action the toolbar control performs. */
export type ToolbarMenuEntry = {
  icon: ReactNode
  label: string
  onSelect: () => void
  /** Reflects the control's pressed state (panel open, filters shown…). */
  active?: boolean
}

export type ToolbarItem = {
  key: string
  node: ReactNode
  /** Item stretches to fill the bar (the search field): budget it by `minWidth`. */
  grow?: boolean
  minWidth?: number
  /**
   * Compact stand-in rendered in the bar when the item does not fit at `minWidth`
   * (the search field becomes a search icon). Without it the item goes to the menu.
   */
  collapsedNode?: ReactNode
  /** Width the compact stand-in takes; defaults to a `h-7 w-7` icon button. */
  collapsedWidth?: number
  /** Always stays in the bar; its width is reserved before the others are placed. */
  pinned?: boolean
  /** Below the `sm` breakpoint the item always lives in the overflow menu, even if it fits. */
  menuOnMobile?: boolean
  /**
   * How the item is offered inside the overflow menu. Items without entries fall
   * back to their toolbar node (the search field, the colour picker, undo/redo…).
   */
  menu?: ToolbarMenuEntry[]
  /**
   * On phones, an item with several `menu` entries is shown as one expandable
   * submenu row with this title (mirroring its desktop dropdown).
   */
  menuTitle?: string
  /** Icon of the submenu row; defaults to the first entry's icon. */
  menuIcon?: ReactNode
  /** Text shown beside the node when an item without `menu` entries sits in the menu. */
  menuLabel?: string
  /**
   * How long the item holds its place in the bar when space runs out: higher stays
   * longer. Items with the same priority leave from the right end first. Defaults to 0.
   */
  priority?: number
  /** Draws a vertical divider before the item, unless it opens the bar. Never shown in the menu. */
  separatorBefore?: boolean
}

/** Tailwind's `sm` breakpoint: below it the bar is considered mobile. */
const MOBILE_QUERY = '(max-width: 639.98px)'

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(MOBILE_QUERY).matches
      : false
  ))
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const media = window.matchMedia(MOBILE_QUERY)
    const onChange = () => setIsMobile(media.matches)
    onChange()
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])
  return isMobile
}

/** gap-0.5 between toolbar items, in px. */
const GAP = 2
/** Width of the overflow (three-dots) button (h-7 w-7) plus its gap. */
const MENU_BUTTON = 28 + GAP
/** Default width of a compact stand-in: a `h-7 w-7` icon button. */
const COLLAPSED_WIDTH = 28
const SAFETY = 2

/** Width an item takes in the bar. Items hidden at the current breakpoint measure 0. */
function measureItem(el: HTMLElement, cap?: number): number {
  let width = 0
  for (const child of Array.from(el.children)) {
    let w = child.getBoundingClientRect().width
    // A growing control is budgeted at its minimum width, whatever it happens to be
    // stretched or squeezed to right now; its divider keeps its own width.
    if (cap !== undefined && child.getClientRects().length > 0 && !('toolbarSeparator' in (child as HTMLElement).dataset)) w = cap
    if (w > 0) width += (width > 0 ? GAP : 0) + w
  }
  return width
}

/**
 * Single-line toolbar: items that do not fit the available width move into a
 * overflow menu anchored at the right end of the bar. Measurement happens in a
 * layout effect (before paint), so the overflowing state is never visible.
 */
export function ToolbarOverflow({ items, className = '', menuLabel, onCollapsedChange, align = 'center', measureKey = '', layer = 'z-30', hoistOnMobile = false, mobileOnly }: {
  items: ToolbarItem[]
  className?: string
  /** `start` for bars whose items grow taller than one line (the formula editor). */
  align?: 'center' | 'start'
  /** Changes whenever the items' contents (not just their keys) change width, to re-measure. */
  measureKey?: string
  /** Stacking layer; a bar whose menus drop over a later bar needs the higher one. */
  layer?: 'z-30' | 'z-40'
  menuLabel: string
  /** Reports which items are currently showing their compact stand-in. */
  onCollapsedChange?: (keys: string[]) => void
  /** On phones, render the menu entries inside the document top bar's ⋮ menu instead of a ⋮ of its own. */
  hoistOnMobile?: boolean
  /** Below `sm`, only these items stay in the bar; every other one goes to the menu. */
  mobileOnly?: string[] | undefined
}) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef(new Map<string, HTMLDivElement>())
  const [hiddenKeys, setHiddenKeys] = useState<string[]>([])
  const [collapsedKeys, setCollapsedKeys] = useState<string[]>([])
  const [measuring, setMeasuring] = useState(true)
  const [menuOpen, setMenuOpen] = useState(false)
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null)

  const isMobile = useIsMobile()
  const topBarHosted = useTopBarMenuHosted()
  const topBarSlot = useTopBarMenuSlot()
  const hoisted = hoistOnMobile && isMobile && topBarHosted
  /** Items pinned to the menu on mobile: never rendered in the bar, never measured. */
  const forcedKeys = new Set(isMobile ? items.filter((item) => item.menuOnMobile || (mobileOnly && !mobileOnly.includes(item.key))).map((item) => item.key) : [])

  const signature = `${items.map((item) => item.key).join('|')}#${measureKey}`
  useLayoutEffect(() => { setMeasuring(true) }, [signature, isMobile, hoisted])

  useEffect(() => {
    const bar = barRef.current
    if (!bar || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => setMeasuring(true))
    observer.observe(bar)
    return () => observer.disconnect()
  }, [])

  // While measuring every item is rendered in the bar, so each one can be measured.
  useLayoutEffect(() => {
    if (!measuring) return
    const bar = barRef.current
    if (!bar) return
    const available = bar.clientWidth
    // A hidden bar (data-hide-while-typing while the keyboard is up) keeps its last
    // layout: re-measuring at zero width would un-collapse items, unmount the search
    // row that opened the keyboard, and loop as the keyboard hides and shows.
    if (available === 0) {
      setMeasuring(false)
      return
    }
    const widths = items.map((item) => {
      if (forcedKeys.has(item.key)) return 0
      const el = itemRefs.current.get(item.key)
      return el ? measureItem(el, item.grow ? item.minWidth : undefined) : 0
    })
    const total = widths.reduce((sum, w) => (w > 0 ? sum + (sum > 0 ? GAP : 0) + w : sum), 0)
    // Pinned items keep the overflow button on screen, so its width is always taken.
    const budget = available - (hoisted ? 0 : MENU_BUTTON) - SAFETY
    const doesNotFit = available > 0 && total > (forcedKeys.size > 0 ? budget : available - SAFETY)
    const overflowing: string[] = []
    const collapsed: string[] = []
    // Pinned items are placed first, whatever their position in the bar.
    let used = items.reduce((sum, item, index) => {
      const w = item.pinned && !forcedKeys.has(item.key) ? widths[index] ?? 0 : 0
      return w > 0 ? sum + (sum > 0 ? GAP : 0) + w : sum
    }, 0)
    // Most important first; equal priorities keep bar order, so the right end leaves first.
    const placementOrder = items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => (b.item.priority ?? 0) - (a.item.priority ?? 0) || a.index - b.index)
    placementOrder.forEach(({ item, index }) => {
      if (item.pinned && !forcedKeys.has(item.key)) return
      if (forcedKeys.has(item.key)) {
        overflowing.push(item.key)
        return
      }
      if (!doesNotFit) return
      const w = widths[index] ?? 0
      if (w <= 0) return
      const candidate = used > 0 ? used + GAP + w : w
      if (candidate <= budget) {
        used = candidate
        return
      }
      // An item with a compact stand-in shrinks instead of leaving the bar, and the
      // items after it keep their chance to fit.
      if (item.collapsedNode) {
        const small = item.collapsedWidth ?? COLLAPSED_WIDTH
        const smallCandidate = used > 0 ? used + GAP + small : small
        if (smallCandidate <= budget) {
          used = smallCandidate
          collapsed.push(item.key)
          return
        }
      }
      overflowing.push(item.key)
    })
    const sameAs = (prev: string[], next: string[]) => prev.length === next.length && prev.every((key, i) => key === next[i])
    const barIndex = new Map(items.map((item, index) => [item.key, index]))
    const byBarOrder = (a: string, b: string) => (barIndex.get(a) ?? 0) - (barIndex.get(b) ?? 0)
    overflowing.sort(byBarOrder)
    collapsed.sort(byBarOrder)
    setHiddenKeys((prev) => (sameAs(prev, overflowing) ? prev : overflowing))
    setCollapsedKeys((prev) => (sameAs(prev, collapsed) ? prev : collapsed))
    setMeasuring(false)
  }, [measuring, items, hoisted])

  useEffect(() => {
    if (!menuOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [menuOpen])

  useEffect(() => { if (hiddenKeys.length === 0) setMenuOpen(false) }, [hiddenKeys.length])
  useEffect(() => { if (!menuOpen && !topBarSlot) setOpenSubmenu(null) }, [menuOpen, topBarSlot])

  const collapsedSignature = collapsedKeys.join('|')
  useEffect(() => {
    onCollapsedChange?.(collapsedSignature ? collapsedSignature.split('|') : [])
    // The callback identity is not part of the signal; only the collapsed set is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsedSignature])

  const hidden = new Set(measuring ? forcedKeys : hiddenKeys)
  // While measuring, every item shows its full node so widths stay comparable.
  const collapsed = new Set(measuring ? [] : collapsedKeys)
  const overflowItems = items.filter((item) => hidden.has(item.key))
  const firstVisibleKey = items.find((item) => !hidden.has(item.key))?.key

  const renderEntry = (entry: ToolbarMenuEntry, key: string, onDone: () => void) => (
    <button
      key={key}
      type="button"
      role="menuitem"
      aria-pressed={entry.active}
      onClick={() => {
        onDone()
        entry.onSelect()
      }}
      className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-surface-3 ${entry.active ? 'text-brand' : ''}`}
    >
      <span className={`flex h-4 w-4 shrink-0 items-center justify-center ${entry.active ? 'text-brand' : 'text-text-muted'}`}>
        {entry.icon}
      </span>
      <span className="min-w-0 flex-1 truncate">{entry.label}</span>
    </button>
  )

  const submenuItem = isMobile ? overflowItems.find((item) => item.key === openSubmenu && item.menuTitle && (item.menu?.length ?? 0) > 1) : undefined

  const renderEntries = (onDone: () => void) => {
    // An open submenu replaces the whole menu (Google Sheets style): a back row with
    // its title, then its entries. `data-submenu-open` lets the host menu hide the rest.
    if (submenuItem) return (
      <div data-submenu-open="" role="menu" aria-label={submenuItem.menuTitle} className="flex flex-col">
        <button
          type="button"
          onClick={() => setOpenSubmenu(null)}
          className="flex w-full items-center gap-2 border-b border-border px-3 py-2.5 text-left font-medium transition-colors hover:bg-surface-3"
        >
          <svg aria-hidden="true" viewBox="0 0 16 16" className="h-4 w-4 shrink-0 fill-none stroke-current text-text-muted" strokeWidth="2"><path d="M13 8H3M7 4L3 8l4 4" /></svg>
          <span className="min-w-0 flex-1 truncate">{submenuItem.menuTitle}</span>
        </button>
        {(submenuItem.menu ?? []).map((entry, index) => renderEntry(entry, `${submenuItem.key}-${index}`, onDone))}
      </div>
    )
    return <>
      {overflowItems.map((item) => {
        const entries = item.menu ?? []
        if (isMobile && item.menuTitle && entries.length > 1) return (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            aria-haspopup="menu"
            onClick={() => setOpenSubmenu(item.key)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-surface-3"
          >
            <span className="flex h-4 w-4 shrink-0 items-center justify-center text-text-muted">{item.menuIcon ?? entries[0]?.icon}</span>
            <span className="min-w-0 flex-1 truncate">{item.menuTitle}</span>
            <svg aria-hidden="true" viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0 fill-none stroke-current text-text-muted" strokeWidth="2"><path d="M6 3l5 5-5 5" /></svg>
          </button>
        )
        return entries.length
          ? entries.map((entry, index) => renderEntry(entry, `${item.key}-${index}`, onDone))
          : <div key={item.key} className="flex items-center gap-1.5 px-3 py-2">{item.node}{item.menuLabel && <span className="min-w-0 flex-1 truncate">{item.menuLabel}</span>}</div>
      })}
    </>
  }

  const setItemRef = (key: string) => (el: HTMLDivElement | null) => {
    if (el) itemRefs.current.set(key, el)
    else itemRefs.current.delete(key)
  }

  return (
    <div ref={wrapRef} data-hide-while-typing className={`relative ${layer} ${className}`}>
      <div ref={barRef} className={`flex min-w-0 gap-0.5 ${align === 'start' ? 'items-start' : 'items-center'}`}>
        {items.map((item) => hidden.has(item.key) ? null : (
          <div key={item.key} ref={setItemRef(item.key)} style={{ display: 'contents' }}>
            {item.separatorBefore && item.key !== firstVisibleKey && <span aria-hidden="true" data-toolbar-separator="" className="flex h-5 w-2.5 shrink-0 justify-center"><span className="h-full w-px bg-border" /></span>}
            {collapsed.has(item.key) ? item.collapsedNode : item.node}
          </div>
        ))}
        {overflowItems.length > 0 && !hoisted && (
          <button
            type="button"
            aria-label={menuLabel}
            title={menuLabel}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className={`ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors ${menuOpen ? 'bg-brand/5 text-brand' : 'hover:bg-surface-hover'}`}
          >
            <svg aria-hidden="true" viewBox="0 0 4 16" className="h-4 w-1 shrink-0 fill-current"><circle cx="2" cy="2.5" r="1.6" /><circle cx="2" cy="8" r="1.6" /><circle cx="2" cy="13.5" r="1.6" /></svg>
          </button>
        )}
      </div>
      {hoisted && topBarSlot && overflowItems.length > 0 && createPortal(
        <div role="group" aria-label={menuLabel} className={`flex flex-col text-sm text-fg ${submenuItem ? '' : 'border-b border-border pb-1 mb-1'}`}>{renderEntries(closeTopBarMenu)}</div>,
        topBarSlot,
      )}
      {menuOpen && !hoisted && overflowItems.length > 0 && (
        <div
          role="menu"
          aria-label={menuLabel}
          className="absolute right-0 top-full z-50 mt-1 flex min-w-52 max-w-[min(20rem,90vw)] flex-col rounded-xl border border-border bg-surface py-1 text-sm sm:text-xs shadow-card text-text"
        >
          {renderEntries(() => setMenuOpen(false))}
        </div>
      )}
    </div>
  )
}
